import React, { Fragment, useCallback } from 'react'
import PropTypes from 'prop-types'
import { HeartIcon, PeopleIconTwo, StarIcon } from 'secondstep-components'
import { domains } from 'config/env'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { buildK8ReportSitePath } from 'components/ReportsPageHeader/constants'
import { K8_REPORT_UNIT_SECTION } from 'pages/LessonCompletionReport/constants'
import {
  RecentActivitiesMetric,
  RecentActivitiesMetricCount,
  RecentActivitiesMetricDescription,
  RecentActivitiesMetricDivider,
  RecentActivitiesMetricIcon,
  RecentActivitiesMetricIconHeart,
  RecentActivitiesMetricLabel,
  RecentActivitiesMetricRow,
  RecentActivitiesPanel,
  RecentActivitiesYellowDot,
} from '../component.styles'
import {
  RecentActivitiesDoneStarIcon,
  RecentActivitiesLink,
  RecentActivitiesLinkWrapper,
  RecentActivitiesMetricContent,
  RecentActivitiesPrimaryButton,
  RecentActivitiesSecondaryButton,
} from './component.styles'
import {
  RECENT_ACTIVITIES_ALL_CLASSES_DESCRIPTION,
  RECENT_ACTIVITIES_ALL_CLASSES_LABEL,
  RECENT_ACTIVITIES_LESSON_14_DAYS_DESCRIPTION,
  RECENT_ACTIVITIES_NO_LESSON_DESCRIPTION,
  RECENT_ACTIVITIES_STAFF_LOGIN_DESCRIPTION,
  SEE_CLASSES_BUTTON_TEXT,
  USER_MANAGEMENT_PATH,
  VIEW_CLASSES_LINK_TEXT,
  VIEW_STAFF_BUTTON_TEXT,
} from '../constants'

const buildMetrics = ({
  classesWithoutLesson,
  onViewClassesClick,
  onViewNoLessonClassesClick,
  onViewRecentLessonClassesClick,
  onViewStaffClick,
  recentLessonClasses,
  staffLoggedIn,
  totalClasses,
}) => {
  const allClassesHaveStarted = totalClasses > 0 && classesWithoutLesson === 0
  const metrics = []

  if (!allClassesHaveStarted) {
    metrics.push({
      buttonText: SEE_CLASSES_BUTTON_TEXT,
      buttonVariant: 'secondary',
      count: classesWithoutLesson,
      description: RECENT_ACTIVITIES_NO_LESSON_DESCRIPTION,
      iconType: 'yellowDot',
      label: 'classes',
      onButtonClick: onViewNoLessonClassesClick,
      testId: 'ra-no-lesson-button',
    })
  }

  metrics.push({
    buttonText: SEE_CLASSES_BUTTON_TEXT,
    buttonVariant: 'secondary',
    count: recentLessonClasses,
    description: RECENT_ACTIVITIES_LESSON_14_DAYS_DESCRIPTION,
    iconType: 'heart',
    label: 'classes',
    onButtonClick: onViewRecentLessonClassesClick,
    testId: 'ra-recent-lesson-button',
  })

  metrics.push({
    buttonText: VIEW_STAFF_BUTTON_TEXT,
    buttonVariant: 'secondary',
    count: staffLoggedIn,
    description: RECENT_ACTIVITIES_STAFF_LOGIN_DESCRIPTION,
    iconType: 'staff',
    label: 'Staff',
    onButtonClick: onViewStaffClick,
    testId: 'ra-staff-button',
  })

  if (allClassesHaveStarted) {
    metrics.push({
      buttonText: VIEW_CLASSES_LINK_TEXT,
      buttonVariant: 'link',
      description: RECENT_ACTIVITIES_ALL_CLASSES_DESCRIPTION,
      iconType: 'done',
      label: RECENT_ACTIVITIES_ALL_CLASSES_LABEL,
      onButtonClick: onViewClassesClick,
      testId: 'ra-all-classes-button',
    })
  }

  return metrics.map((metric, index) => ({
    ...metric,
    buttonVariant: index === 0 ? 'primary' : metric.buttonVariant,
  }))
}

const MetricIcon = ({ iconType }) => {
  if (iconType === 'yellowDot') {
    return (
      <RecentActivitiesMetricIcon>
        <RecentActivitiesYellowDot />
      </RecentActivitiesMetricIcon>
    )
  }

  if (iconType === 'heart') {
    return (
      <RecentActivitiesMetricIconHeart>
        <HeartIcon />
      </RecentActivitiesMetricIconHeart>
    )
  }

  if (iconType === 'done') {
    return (
      <RecentActivitiesMetricIcon>
        <RecentActivitiesDoneStarIcon>
          <StarIcon />
        </RecentActivitiesDoneStarIcon>
      </RecentActivitiesMetricIcon>
    )
  }

  return (
    <RecentActivitiesMetricIcon>
      <PeopleIconTwo />
    </RecentActivitiesMetricIcon>
  )
}

MetricIcon.propTypes = {
  iconType: PropTypes.oneOf(['yellowDot', 'heart', 'staff', 'done']).isRequired,
}

const MetricAction = ({ buttonText, buttonVariant, onButtonClick, testId }) => {
  if (buttonVariant === 'link') {
    return (
      <RecentActivitiesLinkWrapper>
        <RecentActivitiesLink data-testid={testId} onClick={onButtonClick}>
          {buttonText}
        </RecentActivitiesLink>
      </RecentActivitiesLinkWrapper>
    )
  }

  if (buttonVariant === 'primary') {
    return (
      <RecentActivitiesPrimaryButton
        data-testid={testId}
        onClick={onButtonClick}
      >
        <span>{buttonText}</span>
      </RecentActivitiesPrimaryButton>
    )
  }

  return (
    <RecentActivitiesSecondaryButton
      data-testid={testId}
      onClick={onButtonClick}
    >
      <span>{buttonText}</span>
    </RecentActivitiesSecondaryButton>
  )
}

MetricAction.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'link']).isRequired,
  onButtonClick: PropTypes.func,
  testId: PropTypes.string.isRequired,
}

const RecentActivitiesMetricCard = ({
  buttonText,
  buttonVariant,
  count,
  description,
  iconType,
  label,
  onButtonClick,
  testId,
}) => (
  <RecentActivitiesMetric
    data-testid={`playbook-recent-activities-${iconType}-card`}
  >
    <RecentActivitiesMetricContent isDone={iconType === 'done'}>
      <RecentActivitiesMetricRow>
        <MetricIcon iconType={iconType} />
        {count != null && (
          <RecentActivitiesMetricCount
            data-testid={`playbook-recent-activities-${iconType}-count`}
          >
            {count}
          </RecentActivitiesMetricCount>
        )}
        <RecentActivitiesMetricLabel>{label}</RecentActivitiesMetricLabel>
      </RecentActivitiesMetricRow>
      <RecentActivitiesMetricDescription>
        {description}
      </RecentActivitiesMetricDescription>
    </RecentActivitiesMetricContent>
    <MetricAction
      buttonText={buttonText}
      buttonVariant={buttonVariant}
      onButtonClick={onButtonClick}
      testId={testId}
    />
  </RecentActivitiesMetric>
)

RecentActivitiesMetricCard.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'link']).isRequired,
  count: PropTypes.number,
  description: PropTypes.string.isRequired,
  iconType: PropTypes.oneOf(['yellowDot', 'heart', 'staff', 'done']).isRequired,
  label: PropTypes.string.isRequired,
  onButtonClick: PropTypes.func,
  testId: PropTypes.string.isRequired,
}

const PlaybookRecentActivitiesView = ({
  navigationEvent,
  numClassesMarkedLastPeriod,
  numClassesNotMarkedLesson,
  siteId,
  siteUserStats,
  totalClassesCount,
}) => {
  const handleViewStaffClick = useCallback(() => {
    if (!navigationEvent || siteId == null) return

    const userManagementUrl = `${domains.ADMIN_DOMAIN}${USER_MANAGEMENT_PATH}/${siteId}?loggedIn=last7days`
    navigationEvent(userManagementUrl, NavigationHelper.types.OPEN)
  }, [navigationEvent, siteId])

  const navigateToClassesReport = useCallback(
    (extraQueryParams = {}) => {
      if (!navigationEvent || siteId == null) return

      const basePath = buildK8ReportSitePath(
        K8_REPORT_UNIT_SECTION.CORE_UNITS,
        siteId,
      )
      const extraQuery = new URLSearchParams(extraQueryParams).toString()
      const reportsUrl = extraQuery ? `${basePath}&${extraQuery}` : basePath
      navigationEvent(reportsUrl, NavigationHelper.types.PUSH)
    },
    [navigationEvent, siteId],
  )

  const handleViewClassesClick = useCallback(() => {
    navigateToClassesReport({ unit: 'core' })
  }, [navigateToClassesReport])

  const handleViewNoLessonClassesClick = useCallback(() => {
    navigateToClassesReport({ activity: 'none', unit: 'core' })
  }, [navigateToClassesReport])

  const handleViewRecentLessonClassesClick = useCallback(() => {
    navigateToClassesReport({ activity: '14-days', unit: 'core' })
  }, [navigateToClassesReport])

  const metrics = buildMetrics({
    classesWithoutLesson: numClassesNotMarkedLesson ?? 0,
    onViewClassesClick: handleViewClassesClick,
    onViewNoLessonClassesClick: handleViewNoLessonClassesClick,
    onViewRecentLessonClassesClick: handleViewRecentLessonClassesClick,
    onViewStaffClick: handleViewStaffClick,
    recentLessonClasses: numClassesMarkedLastPeriod ?? 0,
    staffLoggedIn: siteUserStats?.usersLoggedInLast7Days ?? 0,
    totalClasses: totalClassesCount ?? 0,
  })

  return (
    <RecentActivitiesPanel data-testid="playbook-recent-activities-panel">
      {metrics.map((metric, index) => (
        <Fragment key={`${metric.iconType}-${metric.label}`}>
          {index > 0 && <RecentActivitiesMetricDivider />}
          <RecentActivitiesMetricCard {...metric} />
        </Fragment>
      ))}
    </RecentActivitiesPanel>
  )
}

PlaybookRecentActivitiesView.propTypes = {
  navigationEvent: PropTypes.func,
  numClassesMarkedLastPeriod: PropTypes.number,
  numClassesNotMarkedLesson: PropTypes.number,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteUserStats: PropTypes.shape({
    usersLoggedInLast7Days: PropTypes.number,
  }),
  totalClassesCount: PropTypes.number,
}

export default PlaybookRecentActivitiesView
